import { useLibEdit } from '@apis/queryHooks/useLibEdit';
import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ImageContainer from '@components/ImageContainer';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import { Validator } from '@components/Validator';
import useToggle from '@hooks/useToggle';
import {
  $curSandTable,
  $sceneMap,
  $sceneMapApi,
  $tourData,
} from '@stores/dataStores';
import {
  type GeneralID,
  type KrpPlayer,
  type KrpPlayerCallbacks,
  LibType,
  type STImageMarker,
  type STMapMarkerScene,
  type SceneData,
  SceneType,
} from '@types';
import { useStore, useStoreMap } from 'effector-react';
import produce from 'immer';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { useEffect, useRef } from 'react';
import { useLocalStorage } from 'react-use';
import { showSceneModal } from '../../../category';
import { loadScene } from '../../../player';
import { onRadarRotate } from '../../actions/onRadarRotate';
import { isSTMarkerScene } from '../../utils/markerTypeCheck';
import ImgNorth from './img-north.png';
import ImgPlay from './img-play.png';
import ImgStop from './img-stop.png';

interface Props {
  data: STImageMarker['data'] | STMapMarkerScene['data'];
  onSelect: (sceneData: SceneData) => void;
  onAngleSet: (angle: number) => void;
  onFovSet?: (angle: number) => void;
}

const PLAYER_ID = 'krp-marker';

function Player({
  data,
  onAngleSet,
  onFovSet,
}: {
  data: SceneData;
  onAngleSet: (angle: number) => void;
  onFovSet?: (angle: number) => void;
}) {
  const videoReady = useToggle(false);
  const videoPlaying = useToggle(false);
  const playerRef = useRef<KrpPlayer | null>(null);
  const dragMode = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (state) => state?.flag?.dragMode ?? 1,
  });
  const northRef = useRef<number>(data.north || 0);
  const [northTip, setNorthTip] = useLocalStorage(
    '720_sandtable_north_setting',
    true
  );

  useEffect(() => {
    const unwatch = onRadarRotate.watch((angle) => {
      playerRef.current?.updateView({ hlookat: angle + northRef.current });
    });
    return () => {
      playerRef.current?.removePlayer();
      unwatch();
    };
  }, []);

  useEffect(() => {
    videoReady.off();
    videoPlaying.off();

    if (playerRef.current) {
      playerRef.current?.removePlayer();
    }

    const callbacks: KrpPlayerCallbacks = {};
    if (data.type === SceneType.pano) {
      callbacks.onViewChange = (view) => {
        handleViewChange(view.hlookat ?? 0);
        onFovSet?.(view.fov);
      };
    } else if (data.type === SceneType.panoVideo) {
      callbacks.onVideoReady = videoReady.on;
      callbacks.onVideoPlay = videoPlaying.on;
      callbacks.onVideoPaused = videoPlaying.off;
      callbacks.onVideoComplete = () => {
        playerRef.current?.playVideo();
      };
    }

    const player = new KrpanoPlayer() as KrpPlayer;
    player.createPlayer(PLAYER_ID, callbacks, () => {
      playerRef.current = player;
      loadScene({
        player,
        data: produce(data, (draft) => {
          draft.view.hLookAt = northRef.current;
        }),
      });
      player.controlMode({ mouse: dragMode ? 'drag' : 'moveto' });
    });
  }, [data.id, data.materialId]);

  const { editLib, editLibLoading } = useLibEdit();

  const handleViewChange = (ath: number) => {
    onAngleSet((ath % 360) - northRef.current);
  };

  const handleSetNorth = async () => {
    if (!playerRef.current) return;
    setNorthTip(false);
    const ath = (playerRef.current.getView().hlookat ?? 0) % 360;
    await editLib({
      libType: LibType.pano,
      id: data.materialId,
      data: { north: ath },
    });
    northRef.current = ath;
    $sceneMapApi.update((draft) => {
      [...draft.values()].forEach((x) => {
        if (x.materialId === data.materialId) {
          x.north = ath;
        }
      });
    });
    handleViewChange(ath);
  };

  const turnNorth = () => {
    playerRef.current?.lookTo({ hlookat: northRef.current });
  };

  const handleToggleVideo = () => {
    if (videoPlaying.isOn) {
      playerRef.current?.pauseVideo();
    } else {
      playerRef.current?.playVideo();
    }
  };

  const renderPanoControls = () => {
    return (
      <>
        <Button
          disabled={editLibLoading}
          className="absolute bottom-15"
          size="sm"
          onClick={handleSetNorth}
        >
          {editLibLoading ? '设置中' : '把当前视角设为正北'}
        </Button>
        <TipWrapper
          className="absolute top-10 right-10"
          text="转到已设置正北视角"
          placement="left"
          size="sm"
        >
          <div
            className="flex h-25 w-25 cursor-pointer items-center justify-center rounded bg-black-black bg-opacity-50 hover:bg-blue hover:bg-opacity-100"
            onClick={turnNorth}
          >
            <img src={ImgNorth} className="h-16 w-16" alt="" />
          </div>
        </TipWrapper>
        {!!northTip && (
          <div
            className="absolute top-10 flex w-200 items-center rounded bg-orange p-10 text-center text-sm text-white"
            onClick={() => setNorthTip(false)}
          >
            拖动全景视角，并设置正北方向，以便与地图雷达进行正确联动
          </div>
        )}
      </>
    );
  };

  const renderVideoControls = () => {
    if (!videoReady.isOn) return;
    return (
      <>
        <img
          src={videoPlaying.isOn ? ImgStop : ImgPlay}
          className="absolute h-32 w-32 cursor-pointer"
          alt=""
          onClick={handleToggleVideo}
        />
        <div className="pointer-events-none absolute bottom-10 flex h-55 w-200 flex-col items-center justify-center rounded bg-black-black bg-opacity-50 text-white">
          <p>因全景视频会动态变化</p>
          <p>所以不支持设置正北方向</p>
        </div>
      </>
    );
  };

  const renderMatrixControls = () => {
    return (
      <div className="pointer-events-none absolute bottom-10 flex h-55 w-200 flex-col items-center justify-center rounded bg-black-black bg-opacity-50 text-white">
        <p>因高清矩阵不能展示360°</p>
        <p>空间所以不支持设置正北方向</p>
      </div>
    );
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="absolute inset-0" id={PLAYER_ID} />
      {data.type === SceneType.pano && renderPanoControls()}
      {data.type === SceneType.panoVideo && renderVideoControls()}
      {data.type === SceneType.matrix && renderMatrixControls()}
    </div>
  );
}

export function SceneSetting({ data, onAngleSet, onFovSet, onSelect }: Props) {
  const sceneMap = useStore($sceneMap);
  const sandTable = useStore($curSandTable);
  const { sceneId } = data;
  const scene = sceneMap.get(sceneId!);

  const handleSelect = async () => {
    const selectedIds = (
      sandTable?.markers.map((x) =>
        isSTMarkerScene(x) ? x.data.sceneId : null
      ) ?? []
    ).filter((x): x is GeneralID => !!x);
    const { idSet } = await showSceneModal({
      title: '选择要标记的场景',
      subTitle: '当场景带有GPS信息时，将自动标记到地图沙盘中。',
      disabledIds: selectedIds,
    });
    if (!idSet) return;
    const id = Array.from(idSet)[0];
    const sceneData = sceneMap.get(id);
    if (!sceneData) return;
    onSelect(sceneData);
  };

  return (
    <Collapse title="标记场景">
      <Validator
        value={scene}
        validFn={() => !!scene}
        warningMessage="请选择标记场景"
      >
        {({ value, warning }) => (
          <ImageContainer
            placeholder="请先选择一个场景"
            className="h-220"
            warning={warning}
          >
            {!!value && (
              <Player
                data={value}
                onAngleSet={onAngleSet}
                onFovSet={onFovSet}
              />
            )}
          </ImageContainer>
        )}
      </Validator>
      <div className="mt-10 flex items-center justify-between">
        <Tip className="mr-20 truncate">
          {scene ? scene.name : '还未选择标记场景'}
        </Tip>
        <Button className="shrink-0" size="sm" onClick={handleSelect}>
          选择场景
        </Button>
      </div>
    </Collapse>
  );
}
